/** Чистая логика «Дефицита»: столбцы матрицы, раскладка ячеек оффера, фильтры и сортировка,
 *  группировка регионов. Классов здесь не вычисляем: цвет — только `deficit_class` строки
 *  витрины; агрегат — счётчики бэкенда. Порогов дней на фронте нет. */
import type {
  DeficitClass,
  WbOffer,
  WbOfferCell,
  WbRegionRef,
  WbRegionRow,
} from "@/api/wbRegions";
import { pct } from "../ozon-analytics/format";
import {
  daysText,
  dec1,
  deficitLabel,
  isWeakConfidence,
  NO_DATA,
  ORDERS_INCOMPLETE,
  qtyText,
  SALES_INCOMPLETE,
} from "./format";
import { ABROAD, UNKNOWN } from "./state";

/** Короткие подписи макрорегионов для столбцов (полное имя — в подсказке). Нет в списке —
 *  полное имя. */
const SHORT: Record<string, string> = {
  Центральный: "Центр",
  "Северо-Западный": "Северо-Запад",
  "Южный и Северо-Кавказский": "Юг и СКФО",
  Приволжский: "Приволжье",
  Уральский: "Урал",
  "Дальневосточный и Сибирский": "ДВ и Сибирь",
};

export function regionLabel(ref: WbRegionRef): string {
  if (ref.region_kind === "unknown") return "Регион не определён";
  return ref.region_wb ?? "(регион без имени)";
}

export interface Column {
  key: string;
  label: string;
  title: string;
}

/** Ключ столбца / фокуса строки витрины; null — вид региона вне контракта (или макрорегион
 *  без имени) — такие строки страница показывает ошибкой, а не теряет. */
export function columnKey(ref: WbRegionRef): string | null {
  if (ref.region_kind === "macro") return ref.region_wb ?? null;
  if (ref.region_kind === "country") return ABROAD;
  if (ref.region_kind === "unknown") return UNKNOWN;
  return null;
}

/** Строки с регионом вне контракта — текст ошибки на каждую (пусто — всё в порядке). */
export function badRegions(refs: WbRegionRef[]): string[] {
  const out = new Set<string>();
  for (const r of refs) {
    if (columnKey(r) === null) out.add(`регион ${String(r.region_wb)} вида ${String(r.region_kind)}`);
  }
  return [...out];
}

/** Столбцы: макрорегионы в порядке /meta (+ встреченные в офферах, которых в /meta нет — не
 *  теряем), затем «Зарубеж» и «Не опр.» — всегда. */
export function buildColumns(metaRegions: WbRegionRef[], offers: WbOffer[]): Column[] {
  const macro: string[] = [];
  const add = (r: WbRegionRef): void => {
    if (r.region_kind === "macro" && r.region_wb && !macro.includes(r.region_wb)) {
      macro.push(r.region_wb);
    }
  };
  metaRegions.forEach(add);
  offers.forEach((o) => o.regions.forEach(add));
  return [
    ...macro.map((m) => ({ key: m, label: SHORT[m] ?? m, title: m })),
    { key: ABROAD, label: "Зарубеж", title: "Страны (не РФ) — каждая своей строкой витрины" },
    { key: UNKNOWN, label: "Не опр.", title: "Регион не определён" },
  ];
}

/** Ячейки оффера по столбцам (у «Зарубежа» — по строке на страну). */
export function cellsByColumn(o: WbOffer): Map<string, WbOfferCell[]> {
  const m = new Map<string, WbOfferCell[]>();
  for (const c of o.regions) {
    const k = columnKey(c);
    if (k === null) continue; // показывается ошибкой страницы (badRegions)
    const list = m.get(k);
    if (list) list.push(c);
    else m.set(k, [c]);
  }
  return m;
}

const DEFICIT: readonly (DeficitClass | null)[] = ["zero", "low"];

/** «Только с дефицитом»: у оффера есть строка витрины с `deficit_class` zero или low. */
export function hasDeficit(o: WbOffer): boolean {
  return o.regions.some((c) => DEFICIT.includes(c.deficit_class));
}

export function filterOffers(
  offers: WbOffer[],
  f: { deficitOnly: boolean; hideNoDemand: boolean },
): WbOffer[] {
  return offers.filter(
    (o) => (!f.deficitOnly || hasDeficit(o)) && (!f.hideNoDemand || !o.total.no_demand),
  );
}

/** Сортировка по фокус-региону. Нет фокуса — порядок бэкенда (спрос оффера). Макрорегион /
 *  «Не опр.» — дни FBO+FBS строки витрины по возрастанию (null и нет строки — в конце).
 *  «Зарубеж» — по спросу стран (Σ заказов/день), по убыванию: дней у группы стран нет. */
export function sortOffers(offers: WbOffer[], focus: string | null): WbOffer[] {
  if (!focus) return offers;
  const keyOf = (o: WbOffer): number | null => {
    const cells = cellsByColumn(o).get(focus) ?? [];
    if (focus === ABROAD) {
      const vals = cells.map((c) => c.orders_28d).filter((v): v is number => v !== null);
      return vals.length ? -vals.reduce((a, b) => a + b, 0) : null;
    }
    return cells[0]?.cover_days_total ?? null;
  };
  return offers
    .map((o, i) => ({ o, i, k: keyOf(o) }))
    .sort((a, b) => {
      if (a.k === null && b.k === null) return a.i - b.i;
      if (a.k === null) return 1;
      if (b.k === null) return -1;
      return a.k - b.k || a.i - b.i;
    })
    .map((x) => x.o);
}

export interface RegionGroups {
  macro: WbRegionRow[];
  countries: WbRegionRow[];
  unknown: WbRegionRow[];
}

/** Регионы таблицы в порядке бэкенда: макро, страны (группа «Зарубеж»), не определён. */
export function groupRegions(rows: WbRegionRow[]): RegionGroups {
  return {
    macro: rows.filter((r) => r.region_kind === "macro"),
    countries: rows.filter((r) => r.region_kind === "country"),
    unknown: rows.filter((r) => r.region_kind === "unknown"),
  };
}

/** Σ двух частей остатка (надёжно = known_chain + by_supply_signal, слабо = weak + unresolved):
 *  обе null — null (нет данных), а не 0. */
export function sum2(a: number | null, b: number | null): number | null {
  if (a === null && b === null) return null;
  return (a ?? 0) + (b ?? 0);
}

/** FBO-оценка строки: уверенность и части «надёжно / слабо» — FBO не факт. */
export function fboTitle(c: WbOfferCell): string {
  const conf = c.confidence === null ? "—" : pct(c.confidence);
  return [
    `FBO ${qtyText(c.fbo_stock)} (оценка), уверенность ${conf}${isWeakConfidence(c.confidence) ? " — слабая" : ""}`,
    `надёжно ${qtyText(sum2(c.fbo_stock_known_chain, c.fbo_stock_by_supply_signal))} · слабо / не опр. ${qtyText(sum2(c.fbo_stock_weak, c.fbo_stock_unresolved))}`,
  ].join("\n");
}

export function cellTitle(c: WbOfferCell): string {
  const inc = [c.orders_complete === false && ORDERS_INCOMPLETE, c.sales_complete === false && SALES_INCOMPLETE].filter(
    Boolean,
  );
  return [
    `${regionLabel(c)}: ${deficitLabel(c.deficit_class)}`,
    `дни FBO+FBS ${daysText(c.cover_days_total, c.orders_28d)}, дни FBO ${daysText(c.cover_days_fbo, c.orders_28d)}`,
    `FBO ${qtyText(c.fbo_stock)} / FBS ${qtyText(c.fbs_stock)} / спрос ${c.orders_28d === null ? NO_DATA : dec1(c.orders_28d)} в день`,
    fboTitle(c),
    ...(inc.length ? [`день неполный: ${inc.join(", ")}`] : []),
  ].join("\n");
}
